// Coordinate scaling for Copilot screenshot capture.
// Algorithm per spec § 15.4 and § 8.
// Caps the long edge at 1568px (Anthropic's recommended range for grounding
// accuracy with the Claude 4.x computer-use models). The Kyoto service is
// pass-through for coordinates; all scaling is browser-side.

export const MAX_LONG_EDGE = 1568;

/**
 * Compute scaled dimensions for a given native display size, capping the
 * long edge at maxLongEdge.
 *
 * @param {{ width: number, height: number }} display - Native display dimensions.
 * @param {number} [maxLongEdge=MAX_LONG_EDGE] - Long-edge pixel cap. The model
 *   reports this per session via recommended_max_long_edge (2576 for Opus
 *   4.8/4.7, else 1568).
 * @returns {{ scaledWidth: number, scaledHeight: number, factorX: number, factorY: number }}
 *   factorX/factorY convert model-space coords back to native pixels:
 *   nativeX = Math.round(modelX * factorX).
 */
export function getScale({ width: nativeWidth, height: nativeHeight }, maxLongEdge = MAX_LONG_EDGE) {
  const longEdge = Math.max(nativeWidth, nativeHeight);
  const downscale = longEdge > maxLongEdge ? maxLongEdge / longEdge : 1;
  const scaledWidth = Math.round(nativeWidth * downscale);
  const scaledHeight = Math.round(nativeHeight * downscale);
  return {
    scaledWidth,
    scaledHeight,
    factorX: nativeWidth / scaledWidth,
    factorY: nativeHeight / scaledHeight,
  };
}

/**
 * Convert a model-space coordinate to native Guacamole pixels.
 * Per spec § 15.4: action coords from the model are in screenshot-scaled space;
 * multiply by factorX/factorY to get native pixels for input injection.
 *
 * @param {[number, number]} coord - [x, y] in model (scaled) space.
 * @param {{ factorX: number, factorY: number }} scale - Result of getScale().
 * @returns {[number, number]} - [x, y] in native pixel space.
 */
export function scaleActionCoord([x, y], scale) {
  return [Math.round(x * scale.factorX), Math.round(y * scale.factorY)];
}
